import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
    baseURL: API,
    headers: { "Content-Type": "application/json" },
});

// Rehydrate auth header from localStorage on first import (before AuthProvider boots)
const storedToken = typeof window !== "undefined" ? window.localStorage.getItem("msh_token") : null;
if (storedToken) {
    api.defaults.headers.common["Authorization"] = `Bearer ${storedToken}`;
}

// Auto-attach admin 2FA session token on every /admin/* call so the gate works transparently
api.interceptors.request.use((config) => {
    const url = config.url || "";
    if (url.startsWith("/admin/") && !url.startsWith("/admin/otp/")) {
        const adminToken = typeof window !== "undefined" ? window.localStorage.getItem("msh_admin_session") : null;
        if (adminToken) {
            config.headers = config.headers || {};
            config.headers["X-Admin-Session"] = adminToken;
        }
    }
    // OTP status endpoint also needs admin token if available (so backend can report verified=true)
    if (url === "/admin/otp/status") {
        const adminToken = typeof window !== "undefined" ? window.localStorage.getItem("msh_admin_session") : null;
        if (adminToken) {
            config.headers = config.headers || {};
            config.headers["X-Admin-Session"] = adminToken;
        }
    }
    return config;
});

export const submitContact = (payload) => api.post("/contact", payload).then((r) => r.data);
export const submitDemoRequest = (payload) => api.post("/demo-request", payload).then((r) => r.data);

// Password reset
export const forgotPassword = (email) => api.post("/auth/forgot-password", { email }).then((r) => r.data);
export const resetPassword = (token, new_password) => api.post("/auth/reset-password", { token, new_password }).then((r) => r.data);

// Billing / Stripe
export const fetchBillingPackages = () => api.get("/billing/packages").then((r) => r.data);
export const fetchBillingSubscription = () => api.get("/billing/subscription").then((r) => r.data);
export const fetchDashboardHistory = (period) => api.get(`/dashboard/history?period=${period}`).then((r) => r.data);
export const createBillingCheckout = (package_id) =>
    api.post("/billing/checkout", { package_id, origin_url: window.location.origin }).then((r) => r.data);
export const fetchBillingCheckoutStatus = (sessionId) =>
    api.get(`/billing/checkout/status/${sessionId}`).then((r) => r.data);
export const createBillingPortalSession = (return_url) =>
    api.post("/billing/portal", { return_url }).then((r) => r.data);
export const changeBillingPlan = (new_package_id) =>
    api.post("/billing/change-plan", { new_package_id, origin_url: window.location.origin }).then((r) => r.data);
export const fetchBillingAddons = () => api.get("/billing/addons").then((r) => r.data);
export const updateBillingAddons = (quantities) =>
    api.post("/billing/addons/update", { quantities, origin_url: window.location.origin }).then((r) => r.data);
export const createTopupCheckout = (topup_id) =>
    api.post("/billing/topup/checkout", { topup_id, origin_url: window.location.origin }).then((r) => r.data);

// Dashboard
export const fetchDashboard = () => api.get("/dashboard").then((r) => r.data);

// Super admin (owner) endpoints
export const fetchAdminOverview = () => api.get("/admin/overview").then((r) => r.data);
export const fetchAdminLeads = (params = {}) => api.get("/admin/leads", { params }).then((r) => r.data);
export const updateAdminLead = (leadId, payload) => api.patch(`/admin/leads/${encodeURIComponent(leadId)}`, payload).then((r) => r.data);
export const deleteAdminLead = (leadId) => api.delete(`/admin/leads/${encodeURIComponent(leadId)}`).then((r) => r.data);

// Platform-admin — tenant (company) management
export const fetchAdminCompanies = (params = {}) => api.get("/admin/companies", { params }).then((r) => r.data);
export const fetchAdminCompany = (id) => api.get(`/admin/companies/${encodeURIComponent(id)}`).then((r) => r.data);
export const fetchAdminTradeCatalog = () => api.get("/admin/companies/_catalog").then((r) => r.data);
export const fetchAdminPricingCatalog = () => api.get("/admin/companies/_pricing").then((r) => r.data);
export const createAdminCompany = (payload) => api.post("/admin/companies", payload).then((r) => r.data);
export const updateAdminCompany = (id, payload) => api.patch(`/admin/companies/${encodeURIComponent(id)}`, payload).then((r) => r.data);
export const patchAdminCompanyNativeFlag = (id, flag, enabled) =>
    api.patch(`/admin/companies/${encodeURIComponent(id)}/native-flags`, { flag, enabled }).then((r) => r.data);
export const patchAdminIntegrationElevenLabs = (id, payload) =>
    api.patch(`/admin/companies/${encodeURIComponent(id)}/integrations/elevenlabs`, payload).then((r) => r.data);
export const manualBlockAdminIntegration = (id, reason) =>
    api.post(`/admin/companies/${encodeURIComponent(id)}/integrations/elevenlabs/block`, { reason: reason || null }).then((r) => r.data);
export const manualReopenAdminIntegration = (id, reason) =>
    api.post(`/admin/companies/${encodeURIComponent(id)}/integrations/elevenlabs/reopen`, { reason: reason || null }).then((r) => r.data);
export const patchAdminBrowserTest = (id, enabled) =>
    api.patch(`/admin/companies/${encodeURIComponent(id)}/integrations/elevenlabs/browser-test`, { enabled }).then((r) => r.data);

// Admin ElevenLabs tools (URLs + rotate + tests) — Sprint 3.5
export const fetchAdminWebhookUrls = (id) =>
    api.get(`/admin/companies/${encodeURIComponent(id)}/integrations/elevenlabs/webhooks`).then((r) => r.data);
export const rotateAdminPreCallSecret = (id) =>
    api.post(`/admin/companies/${encodeURIComponent(id)}/integrations/elevenlabs/pre-call/rotate`).then((r) => r.data);
export const rotateAdminVoiceIntakeSecret = (id) =>
    api.post(`/admin/companies/${encodeURIComponent(id)}/integrations/elevenlabs/voice-intake/rotate`).then((r) => r.data);
export const rotateAdminPostCallSecret = (id) =>
    api.post(`/admin/companies/${encodeURIComponent(id)}/integrations/elevenlabs/post-call/rotate`).then((r) => r.data);
export const saveAdminPostCallSecret = (id, secret) =>
    api.post(`/admin/companies/${encodeURIComponent(id)}/integrations/elevenlabs/post-call/secret`, { secret }).then((r) => r.data);
export const rotateAdminExistingJobManagerSecret = (id) =>
    api.post(`/admin/companies/${encodeURIComponent(id)}/integrations/elevenlabs/existing-job-manager/rotate`).then((r) => r.data);
export const rotateAdminCompanyContextSecret = (id) =>
    api.post(`/admin/companies/${encodeURIComponent(id)}/integrations/elevenlabs/company-context/rotate`).then((r) => r.data);
export const fetchAdminCompanyAiData = (id) =>
    api.get(`/admin/companies/${encodeURIComponent(id)}/integrations/elevenlabs/company-context/ai-data`).then((r) => r.data);
export const rotateAdminTradeContextSecret = (id) =>
    api.post(`/admin/companies/${encodeURIComponent(id)}/integrations/elevenlabs/trade-context/rotate`).then((r) => r.data);

// P0.5.4 — Global Trade Playbooks (super-admin only)
export const fetchTradePlaybooks = () => api.get("/admin/trade-playbooks").then((r) => r.data);
export const fetchTradePlaybook = (trade) => api.get(`/admin/trade-playbooks/${trade}`).then((r) => r.data);
export const patchTradePlaybook = (trade, draft) => api.patch(`/admin/trade-playbooks/${trade}`, { draft }).then((r) => r.data);
export const publishTradePlaybook = (trade) => api.post(`/admin/trade-playbooks/${trade}/publish`).then((r) => r.data);
export const previewTradePlaybook = (trade) => api.get(`/admin/trade-playbooks/${trade}/preview`).then((r) => r.data);
export const testAdminPreCall = (id) =>
    api.post(`/admin/companies/${encodeURIComponent(id)}/integrations/elevenlabs/test/pre-call`).then((r) => r.data);
export const testAdminVoiceIntake = (id) =>
    api.post(`/admin/companies/${encodeURIComponent(id)}/integrations/elevenlabs/test/voice-intake`).then((r) => r.data);
export const testAdminPostCall = (id) =>
    api.post(`/admin/companies/${encodeURIComponent(id)}/integrations/elevenlabs/test/post-call`).then((r) => r.data);

// Admin notifications (platform-admin unified inbox)
export const fetchAdminNotifications = (params = {}) =>
    api.get("/admin/notifications", { params }).then((r) => r.data);
export const fetchAdminNotificationsUnreadCount = () =>
    api.get("/admin/notifications/unread-count").then((r) => r.data);
export const markAdminNotificationRead = (id) =>
    api.patch(`/admin/notifications/${encodeURIComponent(id)}/read`).then((r) => r.data);
export const markAllAdminNotificationsRead = () =>
    api.post("/admin/notifications/read-all", {}).then((r) => r.data);

// Tenant AI-phone panel (Sprint 3 — customer-friendly view of ElevenLabs)
export const fetchTenantAiPhone = () =>
    api.get("/settings/ai-phone").then((r) => r.data);
export const patchTenantForwardPhone = (forward_phone) =>
    api.patch("/settings/ai-phone/forward-phone", { forward_phone }).then((r) => r.data);
export const requestAdminCompanyDeleteOtp = (id) => api.post(`/admin/companies/${encodeURIComponent(id)}/delete-2fa/request`).then((r) => r.data);
export const deleteAdminCompany = (id, payload) => api.delete(`/admin/companies/${encodeURIComponent(id)}`, { data: payload }).then((r) => r.data);

// Admin 2FA OTP
export const requestAdminOtp = () => api.post("/admin/otp/request").then((r) => r.data);
export const verifyAdminOtp = (code) => api.post("/admin/otp/verify", { code }).then((r) => r.data);
export const fetchAdminOtpStatus = () => api.get("/admin/otp/status").then((r) => r.data);
export const logoutAdminOtp = () => api.post("/admin/otp/logout").then((r) => r.data);
export const setAdminSession = (token) => {
    if (token) window.localStorage.setItem("msh_admin_session", token);
};
export const clearAdminSession = () => {
    window.localStorage.removeItem("msh_admin_session");
};

// Company billing profile (used for invoice generation)
export const fetchBillingProfile = () => api.get("/company/billing-profile").then((r) => r.data);
export const updateBillingProfile = (payload) => api.patch("/company/billing-profile", payload).then((r) => r.data);

// Invoices (Fakturaer) — proper invoice system (separate from /invoices = "Faktura-klar jobs")
export const fetchInvoices = (params = {}) => api.get("/fakturaer", { params }).then((r) => r.data);
export const fetchInvoice = (id) => api.get(`/fakturaer/${id}`).then((r) => r.data);
export const createInvoice = (payload) => api.post("/fakturaer", payload).then((r) => r.data);
export const patchInvoice = (id, payload) => api.patch(`/fakturaer/${id}`, payload).then((r) => r.data);
export const reopenInvoice = (id) => api.post(`/fakturaer/${id}/reopen`).then((r) => r.data);
export const deleteInvoice = (id, { confirm = "SLET", reason = "" } = {}) =>
    api.delete(`/fakturaer/${id}`, { params: { confirm, reason } }).then((r) => r.data);
export const markInvoicePaymentMissing = (id) => api.post(`/fakturaer/${id}/mark-payment-missing`).then((r) => r.data);
export const autoBackfillInvoices = () => api.post("/fakturaer/auto-backfill").then((r) => r.data);
export const fetchOverdueInvoiceNotifications = () => api.get("/notifications/overdue-invoices").then((r) => r.data);
// Company-facing notifications (tenant-scoped)
export const fetchCompanyNotifications = (params = {}) =>
    api.get("/notifications", { params }).then((r) => r.data);
export const fetchCompanyUnreadCount = () =>
    api.get("/notifications/unread-count").then((r) => r.data);
export const markCompanyNotificationRead = (id) =>
    api.patch(`/notifications/${encodeURIComponent(id)}/read`).then((r) => r.data);
export const markAllCompanyNotificationsRead = () =>
    api.post("/notifications/read-all", {}).then((r) => r.data);
export const issueInvoice = (id) => api.post(`/fakturaer/${id}/issue`).then((r) => r.data);
export const sendInvoice = (id, payload = {}) => api.post(`/fakturaer/${id}/send`, payload).then((r) => r.data);
export const registerInvoicePayment = (id, payload) => api.post(`/fakturaer/${id}/payments`, payload).then((r) => r.data);
export const deleteInvoicePayment = (id, paymentId) => api.delete(`/fakturaer/${id}/payments/${paymentId}`).then((r) => r.data);
export const invoicePdfUrl = (id) => `${API}/fakturaer/${id}/pdf`;
export const publicInvoiceUrl = (token) => `${API}/public/fakturaer/${token}`;
export const publicInvoicePdfUrl = (token) => `${API}/public/fakturaer/${token}/pdf`;

// Invoice Template (Phase 2)
export const fetchInvoiceTemplate = () => api.get("/company/invoice-template").then((r) => r.data);
export const updateInvoiceTemplate = (payload) => api.patch("/company/invoice-template", payload).then((r) => r.data);
export const fetchInvoiceTemplatePresets = () => api.get("/company/invoice-template/presets").then((r) => r.data);
export const applyInvoiceTemplatePreset = (preset_id) => api.post("/company/invoice-template/apply-preset", { preset_id }).then((r) => r.data);
export const uploadInvoiceTemplateBackground = (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.post("/company/invoice-template/background", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};
export const deleteInvoiceTemplateBackground = () => api.delete("/company/invoice-template/background").then((r) => r.data);
export const invoiceTemplateBackgroundUrl = () => `${API}/company/invoice-template/background?_t=${Date.now()}`;
// Authenticated blob fetchers (img/a tags don't send Bearer header)
export const fetchInvoiceTemplateBackgroundBlob = () =>
    api.get("/company/invoice-template/background", { responseType: "blob" }).then((r) => r.data);
export const fetchInvoiceTemplatePreviewPdfBlob = () =>
    api.get("/fakturaer/template-preview-pdf", { responseType: "blob" }).then((r) => r.data);
export const fetchInvoicePdfBlob = (id) =>
    api.get(`/fakturaer/${id}/pdf`, { responseType: "blob" }).then((r) => r.data);


// Connections / Integrations
export const fetchConnections = () => api.get("/connections").then((r) => r.data);
export const upsertConnection = (payload) => api.post("/connections", payload).then((r) => r.data);
export const testConnection = (type) => api.post(`/connections/${type}/test`).then((r) => r.data);
export const syncConnection = (type) => api.post(`/connections/${type}/sync`).then((r) => r.data);
export const deactivateConnection = (type) => api.post(`/connections/${type}/deactivate`).then((r) => r.data);
export const testEmailSend = (payload) => api.post("/integrations/email/test", payload).then((r) => r.data);
export const fetchIntegrationStatus = () => api.get("/integration-status").then((r) => r.data);
export const fetchElevenLabsWebhookInfo = () => api.get("/connections/elevenlabs/webhook-info").then((r) => r.data);
export const regenerateElevenLabsSecret = (which) => api.post("/connections/elevenlabs/regenerate-secret", { which }).then((r) => r.data);

// Sprint C3 — Native ElevenLabs Settings
export const fetchElevenLabsSettings = () => api.get("/integrations/elevenlabs/settings").then((r) => r.data);
export const updateElevenLabsPostCall = (payload) => api.patch("/integrations/elevenlabs/settings/post-call", payload).then((r) => r.data);
export const rotateVoiceIntakeSecret = () => api.post("/integrations/elevenlabs/voice-intake/rotate").then((r) => r.data);
export const revealVoiceIntakeSecret = () => api.post("/integrations/elevenlabs/voice-intake/reveal").then((r) => r.data);
export const probeElevenLabsConfig = () => api.post("/integrations/elevenlabs/probe").then((r) => r.data);
export const fetchPostCalls = (limit = 50) => api.get(`/post-calls?limit=${limit}`).then((r) => r.data);

// P0.5 — Company AI Profile V1 (tenant-facing; owner/admin edit)
export const fetchAiProfile = () => api.get("/company/ai-profile").then((r) => r.data);
export const patchAiProfile = (payload) => api.patch("/company/ai-profile", payload).then((r) => r.data);
export const patchAiProfileTrades = (payload) => api.patch("/company/ai-profile/trades", payload).then((r) => r.data);
export const publishAiProfile = () => api.post("/company/ai-profile/publish").then((r) => r.data);
export const fetchAiProfilePreview = () => api.get("/company/ai-profile/preview").then((r) => r.data);
export const fetchJobConversation = (jobId) => api.get(`/jobs/${encodeURIComponent(jobId)}/conversation`).then((r) => r.data);

// Owner/admin: medarbejderens fotos (før/efter) + kvitteringer på en opgave
export const fetchJobFiles = (jobId) => api.get(`/jobs/${encodeURIComponent(jobId)}/files`).then((r) => r.data);
export const fetchJobFileBlob = (jobId, fileId) =>
    api.get(`/jobs/${encodeURIComponent(jobId)}/files/${encodeURIComponent(fileId)}`, { responseType: "blob" }).then((r) => r.data);
export const fetchCompanyUsage = () => api.get("/company/usage-snapshot").then((r) => r.data);

// Integration logs + sync payload
export const fetchIntegrationLogs = (params = {}) => api.get("/integration-logs", { params }).then((r) => r.data);
export const clearTestLogs = () => api.delete("/integration-logs").then((r) => r.data);
export const fetchLatestSync = (type) => api.get(`/connections/${type}/latest-sync`).then((r) => r.data);
export const fetchDataSourceSummary = () => api.get("/data-source-summary").then((r) => r.data);

// Data mappings
export const fetchDataMappings = () => api.get("/data-mappings").then((r) => r.data);
export const upsertDataMapping = (payload) => api.post("/data-mappings", payload).then((r) => r.data);
export const previewDataMapping = (entity) => api.get(`/data-mappings/${entity}/preview`).then((r) => r.data);

// Purchase requests (indkøbsliste)
export const fetchPurchaseRequests = () => api.get("/purchase-requests").then((r) => r.data);

// Invitations
export const createInvitation = (payload) => api.post("/invitations", payload).then((r) => r.data);
export const listInvitations = () => api.get("/invitations").then((r) => r.data);
export const fetchInvitation = (token) => api.get(`/invitations/${token}`).then((r) => r.data);

// Materials CRUD
export const fetchMaterials = () => api.get("/materials").then((r) => r.data);
export const createMaterial = (payload) => api.post("/materials", payload).then((r) => r.data);
export const updateMaterial = (id, payload) => api.put(`/materials/${id}`, payload).then((r) => r.data);
export const deleteMaterial = (id) => api.delete(`/materials/${id}`).then((r) => r.data);
export const fetchMaterialUsage30d = (id) => api.get(`/materials/${id}/usage-30d`).then((r) => r.data);

// Jobs CRUD
export const fetchJobs = () => api.get("/jobs").then((r) => r.data);
export const fetchJob = (id) => api.get(`/jobs/${encodeURIComponent(id)}`).then((r) => r.data);
export const findJobSuppliers = (id) => api.post(`/jobs/${encodeURIComponent(id)}/find-suppliers`).then((r) => r.data);
export const selectJobSupplier = (id, payload) => api.post(`/jobs/${encodeURIComponent(id)}/select-supplier`, payload).then((r) => r.data);
export const markJobOrdered = (id, payload) => api.post(`/jobs/${encodeURIComponent(id)}/mark-ordered`, payload).then((r) => r.data);
export const markJobReceived = (id, payload) => api.post(`/jobs/${encodeURIComponent(id)}/mark-received`, payload).then((r) => r.data);
export const fetchSpecialOrders = () => api.get(`/purchase/special-orders`).then((r) => r.data);
export const createJob = (payload) => api.post("/jobs", payload).then((r) => r.data);
export const updateJob = (id, payload) => api.put(`/jobs/${id}`, payload).then((r) => r.data);
export const deleteJob = (id) => api.delete(`/jobs/${id}`).then((r) => r.data);

// Employee (medarbejder) mobile section — same data, server-scoped
export const fetchEmployeeJobs = () => api.get("/employee/jobs").then((r) => r.data);
export const fetchEmployeeJob = (id) => api.get(`/employee/jobs/${encodeURIComponent(id)}`).then((r) => r.data);
export const addEmployeeNote = (id, text) => api.post(`/employee/jobs/${encodeURIComponent(id)}/note`, { text }).then((r) => r.data);
export const setEmployeeStatus = (id, status) => api.post(`/employee/jobs/${encodeURIComponent(id)}/status`, { status }).then((r) => r.data);

// Customers CRUD
export const fetchCustomers = () => api.get("/customers").then((r) => r.data);
export const fetchCustomerMaterials = (customerId) => api.get(`/customers/${customerId}/materials`).then((r) => r.data);
export const createCustomer = (payload) => api.post("/customers", payload).then((r) => r.data);
export const updateCustomer = (id, payload) => api.put(`/customers/${id}`, payload).then((r) => r.data);
export const deleteCustomer = (id) => api.delete(`/customers/${id}`).then((r) => r.data);

// Employees CRUD
export const fetchEmployees = () => api.get("/employees").then((r) => r.data);
export const createEmployee = (payload) => api.post("/employees", payload).then((r) => r.data);
export const updateEmployee = (id, payload) => api.put(`/employees/${id}`, payload).then((r) => r.data);
export const deleteEmployee = (id, reason) => api.delete(`/employees/${id}`, { params: { reason } }).then((r) => r.data);
export const uploadEmployeePhoto = (id, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.post(`/employees/${id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};
export const deleteEmployeePhoto = (id) => api.delete(`/employees/${id}/photo`).then((r) => r.data);
export const getEmployeePhotoUrl = (id) => `${API}/employees/${id}/photo`;

// Invoice-ready jobs (legacy /invoices = "Faktura-klar" page, NOT the proper invoice system above)
export const fetchInvoiceReadyJobs = () => api.get("/invoices").then((r) => r.data);
export const createInvoiceReadyJob = (payload) => api.post("/invoices", payload).then((r) => r.data);
export const updateInvoiceReadyJob = (id, payload) => api.put(`/invoices/${id}`, payload).then((r) => r.data);
export const deleteInvoiceReadyJob = (id) => api.delete(`/invoices/${id}`).then((r) => r.data);

// Shopping list (Phase 2B — Indkøbsliste + Leveringscheckliste, gated by USE_NATIVE_PURCHASE_REQUESTS)
export const fetchShoppingList = (stage = "open") => api.get("/shopping-list", { params: { stage } }).then((r) => r.data);
export const createShoppingItem = (payload) => api.post("/shopping-list", payload).then((r) => r.data);
export const updateShoppingItem = (id, payload) => api.put(`/shopping-list/${id}`, payload).then((r) => r.data);
export const markShoppingPurchased = (id, payload) => api.post(`/shopping-list/${id}/mark-purchased`, payload).then((r) => r.data);
export const markShoppingDelivered = (id, payload) => api.post(`/shopping-list/${id}/mark-delivered`, payload).then((r) => r.data);
export const cancelShoppingItem = (id, reason) => api.post(`/shopping-list/${id}/cancel`, { reason }).then((r) => r.data);
export const fetchDeliveryChecklist = () => api.get("/delivery-checklist").then((r) => r.data);
export const fetchShoppingTotals = () => api.get("/shopping-list/totals").then((r) => r.data);
export const fetchJobPurchaseRequests = (jobId) => api.get(`/jobs/${jobId}/purchase-requests`).then((r) => r.data);
export const triggerAutoRestock = () => api.post("/shopping-list/auto-restock").then((r) => r.data);
export const fetchShoppingBySupplier = () => api.get("/shopping-list/by-supplier").then((r) => r.data);
export const markGroupOrdered = (payload) => api.post("/shopping-list/mark-group-ordered", payload).then((r) => r.data);

export const formatApiError = (err) => {
    const d = err?.response?.data?.detail;
    if (!d) return err?.message || "Noget gik galt. Prøv igen.";
    if (typeof d === "string") return d;
    if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" ");
    return JSON.stringify(d);
};
